import { type ReactNode, type RefObject, useEffect, useMemo } from 'react'
import { Combobox as ComboboxPrimitive } from '@base-ui/react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { CheckIcon } from 'lucide-react'

import { cn } from '@/components/new/lib'

import {
    DEFAULT_VIRTUALIZED_COMBOBOX_GROUP_LABEL_HEIGHT,
    DEFAULT_VIRTUALIZED_COMBOBOX_ITEM_HEIGHT,
    DEFAULT_VIRTUALIZED_COMBOBOX_OVERSCAN,
    DEFAULT_VIRTUALIZED_COMBOBOX_SEPARATOR_HEIGHT,
    hasVisibleGroupLabel,
} from './helpers'

import type { ComboboxFieldGroupByHandler, ComboboxFieldOption } from './types'

type VirtualizedComboboxRow =
    | { key: string; type: 'group-label'; label: string }
    | { key: string; type: 'item'; item: ComboboxFieldOption; itemIndex: number }
    | { key: string; type: 'separator' }

export function ComboboxItem({ className, children, ...props }: ComboboxPrimitive.Item.Props) {
    return (
        <ComboboxPrimitive.Item
            data-slot="combobox-item"
            className={cn(
                "data-highlighted:bg-accent data-highlighted:text-accent-foreground outline-hidden data-disabled:pointer-events-none data-disabled:opacity-50 relative flex w-full cursor-default select-none items-center gap-2 rounded-md py-1 pl-1.5 pr-8 text-sm [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
                className
            )}
            {...props}
        >
            {children}
            <ComboboxPrimitive.ItemIndicator
                render={
                    <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center" />
                }
            >
                <CheckIcon className="pointer-events-none" />
            </ComboboxPrimitive.ItemIndicator>
        </ComboboxPrimitive.Item>
    )
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
    return <ComboboxPrimitive.Group data-slot="combobox-group" className={cn(className)} {...props} />
}

function ComboboxLabel({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) {
    return (
        <ComboboxPrimitive.GroupLabel
            data-slot="combobox-label"
            className={cn('text-muted-foreground truncate whitespace-nowrap px-2 py-1.5 text-xs', className)}
            {...props}
        />
    )
}

export function ComboboxCollection({ ...props }: ComboboxPrimitive.Collection.Props) {
    return <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />
}

export function ComboboxStatus({ className, ...props }: ComboboxPrimitive.Status.Props) {
    return <ComboboxPrimitive.Status data-slot="combobox-status" className={cn(className)} {...props} />
}

function ComboboxSeparator({ className, ...props }: ComboboxPrimitive.Separator.Props) {
    return (
        <ComboboxPrimitive.Separator
            data-slot="combobox-separator"
            className={cn('bg-border -mx-1 my-1 h-px', className)}
            {...props}
        />
    )
}

export function GroupedComboboxCollection({
    groupBy,
    renderOption,
}: {
    groupBy: ComboboxFieldGroupByHandler
    renderOption?: (option: ComboboxFieldOption) => ReactNode
}) {
    const filteredItems = ComboboxPrimitive.useFilteredItems<ComboboxFieldOption>()
    const groupedFilteredItems = useMemo(() => {
        const groups: Array<{ label: string; items: ComboboxFieldOption[] }> = []
        const groupIndexByLabel = new Map<string, number>()

        filteredItems.forEach((option) => {
            const resolvedGroupLabel = groupBy(option)
            const groupLabel = resolvedGroupLabel || ''
            const existingGroupIndex = groupIndexByLabel.get(groupLabel)

            if (existingGroupIndex === undefined) {
                groupIndexByLabel.set(groupLabel, groups.length)
                groups.push({ label: groupLabel, items: [option] })
                return
            }

            groups[existingGroupIndex].items.push(option)
        })

        return groups
    }, [filteredItems, groupBy])

    return groupedFilteredItems.map((group, index) => (
        <ComboboxGroup key={`${group.label}-${index}`} items={group.items}>
            {hasVisibleGroupLabel(group.label) && <ComboboxLabel>{group.label}</ComboboxLabel>}
            <ComboboxCollection>
                {(item: ComboboxFieldOption) => (
                    <ComboboxItem key={item.value} value={item} disabled={item.disabled}>
                        {renderOption ? renderOption(item) : item.label}
                    </ComboboxItem>
                )}
            </ComboboxCollection>
            {index < groupedFilteredItems.length - 1 && <ComboboxSeparator />}
        </ComboboxGroup>
    ))
}

function buildVirtualizedComboboxRows(options: {
    filteredItems: ComboboxFieldOption[]
    groupBy?: ComboboxFieldGroupByHandler
}): VirtualizedComboboxRow[] {
    const { filteredItems, groupBy } = options

    if (!groupBy) {
        return filteredItems.map((item, itemIndex) => ({
            key: `item-${item.value}-${itemIndex}`,
            type: 'item',
            item,
            itemIndex,
        }))
    }

    const rows: VirtualizedComboboxRow[] = []
    let currentGroupLabel: string | null = null

    filteredItems.forEach((item, itemIndex) => {
        const nextGroupLabel = groupBy(item) || ''

        if (currentGroupLabel !== nextGroupLabel) {
            if (rows.length > 0) {
                rows.push({
                    key: `separator-${rows.length}-${currentGroupLabel ?? ''}`,
                    type: 'separator',
                })
            }

            if (hasVisibleGroupLabel(nextGroupLabel)) {
                rows.push({
                    key: `group-${nextGroupLabel}-${rows.length}`,
                    type: 'group-label',
                    label: nextGroupLabel,
                })
            }

            currentGroupLabel = nextGroupLabel
        }

        rows.push({
            key: `item-${item.value}-${itemIndex}`,
            type: 'item',
            item,
            itemIndex,
        })
    })

    return rows
}

export function VirtualizedComboboxCollection({
    groupBy,
    renderOption,
    listRef,
    selectedScrollValue,
    highlightedValue,
    highlightedReason,
}: {
    groupBy?: ComboboxFieldGroupByHandler
    renderOption?: (option: ComboboxFieldOption) => ReactNode
    listRef: RefObject<HTMLDivElement | null>
    selectedScrollValue?: string
    highlightedValue?: ComboboxFieldOption
    highlightedReason?: 'keyboard' | 'pointer' | 'none'
}) {
    const filteredItems = ComboboxPrimitive.useFilteredItems<ComboboxFieldOption>()
    const rows = useMemo(() => buildVirtualizedComboboxRows({ filteredItems, groupBy }), [filteredItems, groupBy])
    const rowMetrics = useMemo(() => {
        let currentOffset = 0

        return rows.map((row) => {
            const size =
                row.type === 'group-label'
                    ? DEFAULT_VIRTUALIZED_COMBOBOX_GROUP_LABEL_HEIGHT
                    : row.type === 'separator'
                      ? DEFAULT_VIRTUALIZED_COMBOBOX_SEPARATOR_HEIGHT
                      : DEFAULT_VIRTUALIZED_COMBOBOX_ITEM_HEIGHT

            const metric = {
                start: currentOffset,
                size,
            }

            currentOffset += size

            return metric
        })
    }, [rows])
    const rowIndexByValue = useMemo(() => {
        const result = new Map<string, number>()

        rows.forEach((row, index) => {
            if (row.type === 'item') {
                result.set(row.item.value, index)
            }
        })

        return result
    }, [rows])
    const initialOffset = useMemo(() => {
        if (!selectedScrollValue) {
            return 0
        }

        const selectedRowIndex = rowIndexByValue.get(selectedScrollValue)
        if (selectedRowIndex === undefined) {
            return 0
        }

        return rowMetrics[selectedRowIndex]?.start ?? 0
    }, [rowIndexByValue, rowMetrics, selectedScrollValue])
    const virtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => listRef.current,
        initialOffset,
        estimateSize: (index) => {
            const row = rows[index]

            if (!row) {
                return DEFAULT_VIRTUALIZED_COMBOBOX_ITEM_HEIGHT
            }

            if (row.type === 'group-label') {
                return DEFAULT_VIRTUALIZED_COMBOBOX_GROUP_LABEL_HEIGHT
            }

            if (row.type === 'separator') {
                return DEFAULT_VIRTUALIZED_COMBOBOX_SEPARATOR_HEIGHT
            }

            return DEFAULT_VIRTUALIZED_COMBOBOX_ITEM_HEIGHT
        },
        getItemKey: (index) => rows[index]?.key ?? index,
        overscan: DEFAULT_VIRTUALIZED_COMBOBOX_OVERSCAN,
    })

    useEffect(() => {
        virtualizer.measure()
    }, [rows, virtualizer])

    useEffect(() => {
        if (!highlightedValue || highlightedReason !== 'keyboard') {
            return
        }

        const highlightedRowIndex = rowIndexByValue.get(highlightedValue.value)

        if (highlightedRowIndex !== undefined) {
            virtualizer.scrollToIndex(highlightedRowIndex, { align: 'auto' })
        }
    }, [highlightedReason, highlightedValue, rowIndexByValue, virtualizer])

    if (rows.length === 0) {
        return null
    }

    return (
        <div className="relative w-full" style={{ height: `${virtualizer.getTotalSize()}px` }}>
            {virtualizer.getVirtualItems().map((virtualRow) => {
                const row = rows[virtualRow.index]

                if (!row) {
                    return null
                }

                return (
                    <div
                        key={row.key}
                        ref={virtualizer.measureElement}
                        data-index={virtualRow.index}
                        className="absolute left-0 top-0 w-full"
                        style={{ transform: `translateY(${virtualRow.start}px)` }}
                    >
                        {row.type === 'group-label' ? (
                            <div
                                data-slot="combobox-label"
                                className="text-muted-foreground truncate whitespace-nowrap px-2 py-1.5 text-xs"
                            >
                                {row.label}
                            </div>
                        ) : row.type === 'separator' ? (
                            <div data-slot="combobox-separator" className="bg-border -mx-1 my-1 h-px" />
                        ) : (
                            <ComboboxItem value={row.item} index={row.itemIndex} disabled={row.item.disabled}>
                                {renderOption ? renderOption(row.item) : row.item.label}
                            </ComboboxItem>
                        )}
                    </div>
                )
            })}
        </div>
    )
}
